/**
 * Phase 530 wave 4 task 4.2 (#1939) — the ONE place a raw `job_parties.status` becomes English.
 *
 * <h3>Why this is a file and not five ternaries in the board</h3>
 * <p>The engine ships KINDS; the portal ships ENGLISH (the phase's Design §3). `job_parties.status`
 * is the engine's own closed column vocabulary — `awaiting_form`, `awaiting_contact`,
 * `awaiting_verification`, `form_received`, `verified`, `staff_cleared`, `failed`, the CHECK set of
 * `db/migrations/058_phase448_job_parties_status_awaiting_contact.sql` — and
 * `platform/core/JobStore/StatusView/JobStatusView.cs` passes it through RAW on purpose, stating
 * that "the chips a staffer reads … are ENGLISH, and English belongs to the surface that renders
 * it" (fence `engine-client-logic`, D4). This module is that surface's half of the deal, written
 * ONCE so the board, the file detail (task 4.3) and anything after them cannot each invent their
 * own word for `awaiting_contact`.</p>
 *
 * <h3>Closed map, explicit fallback — and why the fallback prints the token</h3>
 * <p>{@link PARTY_STATUS_COPY} is closed over today's vocabulary. A status the platform grows
 * LATER — a new member of that CHECK constraint, landed in the engine long before anyone touches
 * this bundle — must not vanish from a staffer's screen, because a party that silently stops being
 * counted is a file that looks clear when it is not. So {@link partyChipFor} falls back to the RAW
 * TOKEN: the chip shows up UGLY (`returned_undeliverable`) rather than INVISIBLY, which is a bug
 * report instead of a compliance incident.</p>
 *
 * <p>That is the run-time half. The build-time half is `FilesBoardPage.test.tsx`, which reads the
 * migration's own CHECK list and fails if any status the column can hold has no English here — so
 * the ugly fallback is the safety net, never the plan.</p>
 *
 * <h3>Design-system `StatusBadge` is deliberately NOT used for these chips</h3>
 * <p>`StatusBadge`'s `STATUS_MAP` carries its own English (`failed` → "Failed", `complete` →
 * "Complete", …). Handing it a raw `job_parties.status` would make the design system a SECOND
 * owner of this vocabulary's copy — exactly what this module exists to prevent, and it would break
 * silently the day the two disagree. The chips are therefore the board's own small span, and the
 * TONE they are painted in is decided here too, beside the word, so a status cannot read as calm
 * in one place and urgent in another. `StatusBadge` renders the STAGE, which is declared copy and
 * not this vocabulary at all.</p>
 */

/**
 * How a chip should read at a glance. Not a colour — a MEANING, resolved to a token-backed class
 * by the page's stylesheet, so a re-theme cannot make "failed" look settled.
 */
export type PartyChipTone =
  /** Nobody can be chased yet — the platform holds no way to reach this party. */
  | "blocked"
  /** Asked, and we are waiting. */
  | "waiting"
  /** Something came back and is being worked through. */
  | "progress"
  /** Settled, nothing owed. */
  | "good"
  /** Settled badly — a human needs to look. */
  | "bad"
  /** A status this portal has no English for yet. See the module doc. */
  | "unknown";

/** One rendered chip: the English, the tone it reads in, and how many parties are in it. */
export interface PartyChip {
  /** The raw `job_parties.status` — a key and a test handle, never rendered copy. */
  status: string;
  /** What a staffer reads. The raw token itself when this portal has no English for it. */
  label: string;
  tone: PartyChipTone;
  count: number;
  /** True when {@link label} is the raw token rather than English — the module doc's case. */
  unmapped: boolean;
}

/**
 * Every `job_parties.status` the column can hold today, in the order the chips render.
 *
 * <p>The order is the file's own PROGRESSION — who cannot be reached, who has been asked, what is
 * coming back, what is settled — with the two settled-badly members last. So a row of chips reads
 * left to right as "how far along are these parties", and the things a staffer can act on are at
 * the start of the line rather than wherever `jsonb_object_agg` happened to put them. (The engine
 * hands us an OBJECT: it has no order to preserve, so one has to be chosen here.)</p>
 */
export const PARTY_STATUS_COPY: Readonly<Record<string, { label: string; tone: PartyChipTone }>> =
  {
    // The platform holds no delivery channel for this party's form (migration 058 — no email on
    // the Companies-House-derived record, or the send itself failed). A person has to find one.
    awaiting_contact: { label: "contact needed", tone: "blocked" },
    // Registered and the form has gone out; it has not come back.
    awaiting_form: { label: "link sent", tone: "waiting" },
    // The form is back and its verification check has not resolved.
    awaiting_verification: { label: "checking", tone: "progress" },
    form_received: { label: "form in", tone: "progress" },
    verified: { label: "verified", tone: "good" },
    // A named human settled the party WITHOUT its form (ClearPartyTool). Settled, and said so.
    staff_cleared: { label: "cleared by staff", tone: "good" },
    failed: { label: "failed", tone: "bad" },
  };

/** The render order — the map's own key order, which is the progression documented above. */
const PARTY_STATUS_ORDER: readonly string[] = Object.keys(PARTY_STATUS_COPY);

/**
 * The English for one raw `job_parties.status`, or the RAW TOKEN when this portal has no English
 * for it yet.
 *
 * <p>Phase 530 wave 4 task 4.3 lifted this out of {@link partyChipFor} because a second surface
 * needs the WORD without the chip: the file detail's timeline renders a `party_outcome` milestone
 * as "&lt;party&gt;: verified", and that milestone's `outcome` is this very column's value
 * (`StatusMilestoneKinds.CarriesOutcome` — "Raw for the same reason
 * `JobStatusRow.PartyStatusCounts` is: the English belongs to the surface"). A ternary in
 * `milestoneCopy.ts` would have made that module a SECOND owner of this vocabulary, which is
 * precisely what this file's header says it exists to prevent — so the lookup is a function and
 * both callers go through it.</p>
 */
export function partyStatusLabel(status: string): string {
  return PARTY_STATUS_COPY[status]?.label ?? status;
}

/**
 * One status and its count, as a chip.
 *
 * <p>An unrecognised status keeps its raw token as the label and is marked {@link
 * PartyChip.unmapped} so the page can say, in its own words, that this is a status the portal does
 * not have English for — rather than pretending the token is copy.</p>
 */
export function partyChipFor(status: string, count: number): PartyChip {
  const known = PARTY_STATUS_COPY[status];
  if (known) {
    return { status, label: known.label, tone: known.tone, count, unmapped: false };
  }
  return { status, label: partyStatusLabel(status), tone: "unknown", count, unmapped: true };
}

/**
 * Every chip for one file's `party_status_counts`, in {@link PARTY_STATUS_ORDER}, with any status
 * this portal does not know appended in a stable alphabetical tail.
 *
 * <p>A zero count is dropped: the engine sends an EMPTY map for a file with no parties (never a
 * map of zeros — `JobStatusView.cs`), so a zero here could only come from a future producer, and
 * "0 verified" is noise rather than news. A file with no parties therefore yields NO chips, and
 * the page says "no parties yet" in words — "nobody is registered" and "three parties, none
 * waiting" stay different facts, which is the distinction the wire shape was built to keep.</p>
 */
export function partyChipsFor(counts: Readonly<Record<string, number>>): PartyChip[] {
  const known = PARTY_STATUS_ORDER.filter((status) => (counts[status] ?? 0) > 0);
  const unknown = Object.keys(counts)
    .filter((status) => !(status in PARTY_STATUS_COPY) && (counts[status] ?? 0) > 0)
    .sort();
  return [...known, ...unknown].map((status) => partyChipFor(status, counts[status] ?? 0));
}

/**
 * Walk 3 cold review — a CORPORATE OWNER (a company that owns the client) is `verified` on the
 * ledger only because no individual identity check applies to a company. Painting that as a green
 * "verified" would tell a staffer it was checked, which it was not. So its chip says what is true
 * — "company owner: no individual check" — plus what its OWN company screen actually found, from
 * the engine's raw `company_screen` word: `not_screened` (skipped for want of a UK company number,
 * or the screen failed), `clear`, or a match. Only a clear screen reads calm; a match reads bad;
 * no screen reads as waiting on a human (they screen it by hand).
 */
export const COMPANY_SCREEN_COPY: Readonly<Record<string, { label: string; tone: PartyChipTone }>> =
  {
    not_screened: { label: "not screened", tone: "waiting" },
    clear: { label: "company screen clear", tone: "progress" },
    match_found: { label: "company screen match", tone: "bad" },
    match: { label: "company screen match", tone: "bad" },
    check_failed: { label: "company screen couldn't run", tone: "bad" },
  };

export const CORPORATE_OWNER_LABEL = "company owner: no individual check";

/**
 * The chip for one party row: a corporate owner gets {@link CORPORATE_OWNER_LABEL} plus its
 * company-screen result (never a green "verified"); every other party is {@link partyChipFor}.
 */
export function partyRowChipFor(party: {
  party_type: string;
  status: string;
  company_screen?: string | null;
}): PartyChip {
  if (party.party_type !== "corporate_owner" || party.status !== "verified") {
    return partyChipFor(party.status, 1);
  }
  const screen = party.company_screen ?? "not_screened";
  const known = COMPANY_SCREEN_COPY[screen];
  const screenLabel = known?.label ?? `company screen: ${screen}`;
  return {
    status: "corporate_owner",
    label: `${CORPORATE_OWNER_LABEL}, ${screenLabel}`,
    tone: known?.tone ?? "unknown",
    count: 1,
    unmapped: known === undefined,
  };
}
